import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Heart, Sparkles } from 'lucide-react';
import { fallbackYdelser, sortServices } from '../data';

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/ydelser');
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setServices(sortServices(data));
          } else {
            setServices(sortServices(fallbackYdelser));
          }
        } else {
          setServices(sortServices(fallbackYdelser));
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices(sortServices(fallbackYdelser));
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="ydelser" className="pt-6 md:pt-8 pb-12 md:pb-14 bg-[#EDE6DF]/30 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-14">
          <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#4A4A4A] font-light mb-6">
            Jeg hjælper dig med
          </h2>
          <div className="w-12 h-[1px] bg-[#BFC8BE] mx-auto mb-6" />
          <p className="font-sans-clean text-base text-[#4A4A4A]/70 leading-relaxed">
            Ingen udfordringer er for små eller for store. Sammen skaber vi et trygt rum, hvor vi kan undersøge dine mønstre, skabe forandring og finde veje til større livsglæde og balance.
          </p>
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-pulse flex flex-col items-center gap-4">
              <div className="w-10 h-10 rounded-full border-2 border-t-[#BFC8BE] border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              <p className="font-sans-clean text-xs tracking-widest uppercase text-[#4A4A4A]/50">Henter ydelser...</p>
            </div>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10"
          >
            {services.map((service, index) => {
              // Extract values safely supporting localized and fallback structures
              const title = service.titel?.da || service.titel || '';
              const shortDesc = service.kort_beskrivelse?.da || service.kort_beskrivelse || '';
              const image = service.billede?.[0] || 'https://assets.auxodev.dk/assets/4291744f-0a58-4691-8a7f-39ecbae78b95/6fe25267-fac3-4f86-ad2f-975d9bdf41da.jpg';
              const slug = service.ydelse_slug?.da || service.ydelse_slug || service.id;

              return (
                <motion.div
                  key={service.id || index}
                  variants={cardVariants}
                  whileHover={{ y: -6 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-[#EDE6DF]/40 flex flex-col h-full group"
                >
                  {/* Image Container */}
                  <Link to={`/ydelse/${slug}`} className="relative h-64 overflow-hidden block">
                    <img
                      src={image}
                      alt={title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                  </Link>

                  {/* Content */}
                  <div className="p-8 flex flex-col flex-grow items-start text-left">
                    <Link to={`/ydelse/${slug}`} className="block group">
                      <h3 className="font-serif-elegant text-2xl text-[#4A4A4A] font-light mb-3 group-hover:text-[#BFC8BE] transition-colors duration-300">
                        {title}
                      </h3>
                    </Link>
                    <p className="font-sans-clean text-sm text-[#4A4A4A]/70 leading-relaxed mb-6 flex-grow">
                      {shortDesc}
                    </p>
                    <Link
                      to={`/ydelse/${slug}`}
                      className="inline-flex items-center gap-1.5 text-xs tracking-widest uppercase text-[#4A4A4A] font-semibold group-hover:text-[#BFC8BE] transition-colors duration-300 focus:outline-none"
                    >
                      Læs mere og book
                      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

      </div>
    </section>
  );
}
